import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { Layout, Paper } from "$components";
import useJStyles from "$hooks/useJStyles";
import {
  DataTableWithContext,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { getEntrepots } from "$services/Entrepot.service";
import { appState } from "$reducers";
import useAuth from "$hooks/useAuth";
import { CacheInvalidationAction } from "$utils/cache";

const useMakeCell = (value, type = undefined) => {
  const navigate = useNavigate();
  const link = ({ _id }) => `/entrepot/${_id}`;

  return {
    type,
    link,
    value,
    onClick: (row) => {
      navigate(link(row));
    },
  };
};

function Entrepot() {
  const { jclasses } = useJStyles();
  const params = useQueryParameters();
  const { isAdmin } = useAuth();
  const { filter: companySidebarFilter } = useSelector(appState);

  const headerData = [
    {
      id: "name",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Entrepôt",
        placeholder: "Par nom",
        value: params.get("name"),
      },
      width: 260,
      cell: { ...useMakeCell(({ name }) => name, EnumDataRowType.Link) },
    },
    {
      id: "adress",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "adresse",
        placeholder: "",
        value: params.get("adress"),
      },
      width: 500,
      cell: { ...useMakeCell(({ location }) => location.adress) },
    },
    {
      id: "cp",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "code postal",
        placeholder: "",
        value: params.get("cp"),
      },
      width: 80,
      cell: { ...useMakeCell(({ location }) => location.cp) },
    },
    {
      id: "city",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "ville",
        placeholder: "",
        value: params.get("city"),
      },
      width: 200,
      cell: { ...useMakeCell(({ location }) => location.city) },
    },
    {
      id: "nbrEnt",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "nb. entrep.",
        placeholder: "",
        value: params.get("nbrEnt"),
      },
      width: 80,
      cell: { ...useMakeCell(({ companies }) => companies.length) },
    },
  ];

  const externalState = useMemo(() => {
    const returnState = {};

    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }
    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: {
        ...filters,
        ...(isAdmin() &&
          companySidebarFilter?.name && {
            company: companySidebarFilter?._id,
          }),
      },
      pagination: { page, rowsPerPage },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, companySidebarFilter]);

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title="Liste des entrepôts"
        contentClassName={jclasses.jLayoutCustom}
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            rowsPerPage={50}
            thead={headerData}
            queryFunction={getEntrepots}
            queryFunctionName={CacheInvalidationAction.GET_ENTREPOTS}
            data-testid="warehouseList"
          />
        </Paper>
      </Layout>
    </Box>
  );
}

export default Entrepot;
